import React, { useEffect, useState, useRef } from "react";
import { api, apiErrorMessage, formatCurrency, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasRole } from "@/lib/roles";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Plus, MagnifyingGlass, Pencil, Archive, Upload, QrCode, X, ImageSquare, Barcode as BarcodeIcon, ArrowsClockwise } from "@phosphor-icons/react";
import { QRCodeSVG } from "qrcode.react";
import Barcode from "react-barcode";
import { toast } from "sonner";

const EMPTY_FORM = {
    name: "", sku: "", barcode: "", category_id: "", description: "",
    purchase_price: 0, selling_price: 0, wholesale_price: 0,
    gst_percent: 18, hsn_code: "",
    current_stock: 0, min_stock: 5, max_stock: 100,
    supplier_id: "", storage_location: "",
    images: [], is_active: true,
};

export default function Products() {
    const { user } = useAuth();
    const canWrite = hasRole(user, "manager");
    const canArchive = hasRole(user, "admin");

    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [search, setSearch] = useState("");
    const [categoryFilter, setCategoryFilter] = useState("all");
    const [stockFilter, setStockFilter] = useState("all");
    const [loading, setLoading] = useState(true);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    const [qrProduct, setQrProduct] = useState(null);
    const fileInput = useRef(null);

    const loadAll = async () => {
        setLoading(true);
        try {
            const params = {};
            if (search) params.search = search;
            if (categoryFilter !== "all") params.category_id = categoryFilter;
            if (stockFilter !== "all") params.stock_status = stockFilter;
            const [p, c, s] = await Promise.all([
                api.get("/products", { params }),
                api.get("/categories"),
                api.get("/suppliers"),
            ]);
            setProducts(p.data);
            setCategories(c.data);
            setSuppliers(s.data);
        } catch (e) {
            toast.error(apiErrorMessage(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [search, categoryFilter, stockFilter]);

    const openCreate = () => {
        setEditing(null);
        setForm(EMPTY_FORM);
        setDialogOpen(true);
    };
    const openEdit = (p) => {
        setEditing(p);
        setForm({ ...EMPTY_FORM, ...p, category_id: p.category_id || "", supplier_id: p.supplier_id || "" });
        setDialogOpen(true);
    };

    const uploadImage = async (fileList) => {
        for (const f of fileList) {
            const fd = new FormData();
            fd.append("file", f);
            try {
                const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
                setForm((prev) => ({ ...prev, images: [...prev.images, data.path] }));
                toast.success("Image uploaded");
            } catch (e) {
                toast.error(apiErrorMessage(e));
            }
        }
    };

    const removeImage = (path) => {
        setForm((prev) => ({ ...prev, images: prev.images.filter((p) => p !== path) }));
    };

    const submit = async () => {
        if (!form.name.trim() || !form.sku.trim()) {
            toast.error("Name and SKU are required");
            return;
        }
        setSaving(true);
        try {
            const payload = { ...form };
            ["purchase_price", "selling_price", "wholesale_price", "gst_percent", "current_stock", "min_stock", "max_stock"]
                .forEach((k) => { payload[k] = Number(payload[k] || 0); });
            payload.category_id = payload.category_id || null;
            payload.supplier_id = payload.supplier_id || null;
            if (editing) {
                delete payload.current_stock; // stock updated via inventory only
                await api.patch(`/products/${editing.id}`, payload);
                toast.success("Product updated");
            } else {
                await api.post("/products", payload);
                toast.success("Product created");
            }
            setDialogOpen(false);
            loadAll();
        } catch (e) {
            toast.error(apiErrorMessage(e));
        } finally {
            setSaving(false);
        }
    };

    const archive = async (p) => {
        if (!window.confirm(`Archive "${p.name}"? Product hidden but data preserved.`)) return;
        try {
            await api.delete(`/products/${p.id}`);
            toast.success("Product archived");
            loadAll();
        } catch (e) {
            toast.error(apiErrorMessage(e));
        }
    };

    const hardDelete = async (p) => {
        if (!window.confirm(`⚠ PERMANENTLY delete "${p.name}"? This cannot be undone.`)) return;
        try {
            await api.delete(`/products/${p.id}?hard=true`);
            toast.success("Product deleted");
            loadAll();
        } catch (e) {
            toast.error(apiErrorMessage(e));
        }
    };

    return (
        <div className="space-y-6" data-testid="products-page">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
                <div>
                    <div className="eyebrow mb-2">Catalog</div>
                    <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight font-display">Products</h1>
                    <p className="text-sm text-neutral-500 mt-1.5">{products.length} product{products.length !== 1 && "s"} in inventory.</p>
                </div>
                {canWrite && (
                    <Button onClick={openCreate} className="text-white h-11" style={{ backgroundColor: "#15803D" }} data-testid="products-add-btn">
                        <Plus size={16} weight="bold" className="mr-1.5" /> New Product
                    </Button>
                )}
            </div>

            {/* Filters */}
            <div className="flex flex-col md:flex-row gap-3">
                <div className="relative flex-1">
                    <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                    <Input
                        placeholder="Search name, SKU, barcode..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="pl-9 h-11"
                        data-testid="products-search-input"
                    />
                </div>
                <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                    <SelectTrigger className="w-full md:w-56 h-11" data-testid="products-category-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All Categories</SelectItem>
                        {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                </Select>
                <Select value={stockFilter} onValueChange={setStockFilter}>
                    <SelectTrigger className="w-full md:w-44 h-11" data-testid="products-stock-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All Stock</SelectItem>
                        <SelectItem value="in_stock">In Stock</SelectItem>
                        <SelectItem value="low">Low Stock</SelectItem>
                        <SelectItem value="out">Out of Stock</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {/* Products grid */}
            {loading ? (
                <div className="text-sm text-neutral-500" data-testid="products-loading">Loading products...</div>
            ) : products.length === 0 ? (
                <EmptyState onCreate={canWrite ? openCreate : null} />
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {products.map((p) => (
                        <ProductCard key={p.id} product={p} onEdit={() => openEdit(p)} onArchive={() => archive(p)}
                                     onDelete={() => hardDelete(p)}
                                     onShowQr={() => setQrProduct(p)} canWrite={canWrite} canArchive={canArchive} />
                    ))}
                </div>
            )}

            {/* Create/Edit dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="product-dialog">
                    <DialogHeader>
                        <DialogTitle className="text-2xl font-extrabold font-display tracking-tight">
                            {editing ? "Edit Product" : "New Product"}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Field label="Name" required>
                            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="product-form-name" />
                        </Field>
                        <Field label="SKU" required>
                            <Input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} data-testid="product-form-sku" />
                        </Field>
                        <Field label="Barcode">
                            <Input value={form.barcode} onChange={(e) => setForm({ ...form, barcode: e.target.value })} placeholder={editing ? "" : "Auto-generate on save (leave blank)"} data-testid="product-form-barcode" />
                        </Field>
                        <Field label="HSN Code">
                            <Input value={form.hsn_code} onChange={(e) => setForm({ ...form, hsn_code: e.target.value })} data-testid="product-form-hsn" />
                        </Field>
                        <Field label="Category">
                            <Select value={form.category_id || "none"} onValueChange={(v) => setForm({ ...form, category_id: v === "none" ? "" : v })}>
                                <SelectTrigger data-testid="product-form-category"><SelectValue placeholder="Select category" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">— None —</SelectItem>
                                    {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </Field>
                        <Field label="Supplier">
                            <Select value={form.supplier_id || "none"} onValueChange={(v) => setForm({ ...form, supplier_id: v === "none" ? "" : v })}>
                                <SelectTrigger data-testid="product-form-supplier"><SelectValue placeholder="Select supplier" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">— None —</SelectItem>
                                    {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.company}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </Field>
                        <Field label="Purchase Price (₹)">
                            <Input type="number" value={form.purchase_price} onChange={(e) => setForm({ ...form, purchase_price: e.target.value })} data-testid="product-form-purchase-price" />
                        </Field>
                        <Field label="Selling Price (₹)">
                            <Input type="number" value={form.selling_price} onChange={(e) => setForm({ ...form, selling_price: e.target.value })} data-testid="product-form-selling-price" />
                        </Field>
                        <Field label="Wholesale Price (₹)">
                            <Input type="number" value={form.wholesale_price} onChange={(e) => setForm({ ...form, wholesale_price: e.target.value })} data-testid="product-form-wholesale-price" />
                        </Field>
                        <Field label="GST (%)">
                            <Input type="number" value={form.gst_percent} onChange={(e) => setForm({ ...form, gst_percent: e.target.value })} data-testid="product-form-gst" />
                        </Field>
                        {!editing && (
                            <Field label="Initial Stock">
                                <Input type="number" value={form.current_stock} onChange={(e) => setForm({ ...form, current_stock: e.target.value })} data-testid="product-form-current-stock" />
                            </Field>
                        )}
                        {editing && (
                            <Field label={`Current Stock: ${editing.current_stock}`}>
                                <div className="space-y-2">
                                    <div className="flex gap-2">
                                        <Input type="number" placeholder="Quantity" value={form._addStock || ""} onChange={(e) => setForm({ ...form, _addStock: e.target.value })} data-testid="product-form-stock-qty" />
                                        <Select value={form._stockMode || "in"} onValueChange={(v) => setForm({ ...form, _stockMode: v })}>
                                            <SelectTrigger className="w-[130px]" data-testid="product-form-stock-mode"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="in">➕ Add</SelectItem>
                                                <SelectItem value="out">➖ Remove</SelectItem>
                                                <SelectItem value="adjustment">🎯 Set to</SelectItem>
                                            </SelectContent>
                                        </Select>
                                        <Button variant="outline" onClick={async () => {
                                            const qty = Number(form._addStock || 0);
                                            const mode = form._stockMode || "in";
                                            if (mode !== "adjustment" && qty <= 0) return toast.error("Quantity > 0 daalein");
                                            if (mode === "adjustment" && qty < 0) return toast.error("Set value 0 ya positive hona chahiye");
                                            try {
                                                await api.post("/inventory/move", {
                                                    product_id: editing.id,
                                                    quantity: qty,
                                                    movement_type: mode,
                                                    remarks: `Manual ${mode} via product edit`,
                                                });
                                                const label = mode === "in" ? `+${qty} added` : mode === "out" ? `-${qty} removed` : `set to ${qty}`;
                                                toast.success(`Stock ${label}`);
                                                setForm({ ...form, _addStock: "" });
                                                loadAll();
                                            } catch (e) { toast.error(apiErrorMessage(e)); }
                                        }} className="whitespace-nowrap" data-testid="product-form-apply-stock-btn">Apply</Button>
                                    </div>
                                    <p className="text-[11px] text-neutral-500">
                                        <span className="font-semibold">Add</span>: stock badhaayein · <span className="font-semibold">Remove</span>: stock ghataayein · <span className="font-semibold">Set to</span>: stock ko exact number pe fix karein
                                    </p>
                                </div>
                            </Field>
                        )}
                        <Field label="Min Stock">
                            <Input type="number" value={form.min_stock} onChange={(e) => setForm({ ...form, min_stock: e.target.value })} data-testid="product-form-min-stock" />
                        </Field>
                        <Field label="Max Stock">
                            <Input type="number" value={form.max_stock} onChange={(e) => setForm({ ...form, max_stock: e.target.value })} data-testid="product-form-max-stock" />
                        </Field>
                        <Field label="Storage Location">
                            <Input value={form.storage_location} onChange={(e) => setForm({ ...form, storage_location: e.target.value })} data-testid="product-form-storage" />
                        </Field>
                    </div>
                    <Field label="Description" className="mt-2">
                        <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} data-testid="product-form-description" />
                    </Field>
                    <div>
                        <div className="eyebrow mb-2">Images</div>
                        <div className="flex flex-wrap gap-3">
                            {form.images.map((img) => (
                                <div key={img} className="relative w-20 h-20 rounded overflow-hidden border border-neutral-200 bg-neutral-50">
                                    <img src={fileUrl(img)} alt="product" className="w-full h-full object-cover" />
                                    <button onClick={() => removeImage(img)} className="absolute -top-1.5 -right-1.5 bg-black text-white rounded-full p-0.5">
                                        <X size={10} weight="bold" />
                                    </button>
                                </div>
                            ))}
                            <button
                                type="button"
                                onClick={() => fileInput.current?.click()}
                                className="w-20 h-20 border-2 border-dashed border-neutral-300 rounded flex flex-col items-center justify-center text-neutral-500 hover:border-neutral-900 hover:text-neutral-900 transition-colors"
                                data-testid="product-form-upload-btn"
                            >
                                <Upload size={18} weight="bold" />
                                <span className="text-[10px] mt-1 font-semibold">Upload</span>
                            </button>
                            <input ref={fileInput} type="file" accept="image/*" multiple hidden onChange={(e) => uploadImage(Array.from(e.target.files || []))} />
                        </div>
                    </div>
                    <div className="flex items-center gap-3 mt-2">
                        <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="product-form-active" />
                        <Label className="text-sm">Active</Label>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="product-form-cancel">Cancel</Button>
                        <Button onClick={submit} disabled={saving} className="text-white" style={{ backgroundColor: "#15803D" }} data-testid="product-form-submit">
                            {saving ? "Saving..." : editing ? "Save Changes" : "Create Product"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* QR + Barcode dialog */}
            <Dialog open={!!qrProduct} onOpenChange={(v) => !v && setQrProduct(null)}>
                <DialogContent className="max-w-md" data-testid="qr-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display">{qrProduct?.name}</DialogTitle>
                    </DialogHeader>
                    {qrProduct && (
                        <div className="flex flex-col items-center py-2 gap-4 print:gap-2">
                            <div className="text-[10px] font-bold uppercase tracking-widest text-neutral-500">SKU · {qrProduct.sku}</div>

                            {/* Visual Barcode */}
                            {qrProduct.barcode ? (
                                <div className="bg-white p-2 border border-neutral-200 rounded-md">
                                    <Barcode
                                        value={qrProduct.barcode}
                                        format={qrProduct.barcode.length === 13 ? "EAN13" : "CODE128"}
                                        height={60}
                                        width={2}
                                        fontSize={14}
                                        margin={4}
                                        background="#ffffff"
                                        lineColor="#0A0A0A"
                                    />
                                </div>
                            ) : (
                                <div className="text-xs text-neutral-500 italic">No barcode assigned yet</div>
                            )}

                            <div className="flex items-center gap-6">
                                <QRCodeSVG value={qrProduct.sku} size={110} bgColor="#ffffff" fgColor="#0A0A0A" />
                                <div className="text-xs text-neutral-600 space-y-1">
                                    <div className="font-mono font-bold">{qrProduct.barcode || "—"}</div>
                                    <div className="text-[10px] text-neutral-500">QR encodes SKU</div>
                                    <div className="text-[10px] text-neutral-500">Barcode: EAN-13</div>
                                </div>
                            </div>

                            <div className="flex gap-2 w-full pt-2">
                                <Button
                                    variant="outline"
                                    className="flex-1"
                                    onClick={async () => {
                                        try {
                                            const { data } = await api.post(`/products/${qrProduct.id}/regenerate-barcode`);
                                            setQrProduct({ ...qrProduct, barcode: data.barcode });
                                            toast.success("New barcode generated");
                                            loadAll();
                                        } catch (e) { toast.error(apiErrorMessage(e)); }
                                    }}
                                    data-testid="qr-regenerate-btn"
                                >
                                    <ArrowsClockwise size={14} className="mr-1.5" /> Regenerate
                                </Button>
                                <Button
                                    onClick={() => window.print()}
                                    className="flex-1 text-white"
                                    style={{ backgroundColor: "#15803D" }}
                                    data-testid="qr-print-btn"
                                >
                                    Print Label
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}

function Field({ label, required, children, className }) {
    return (
        <div className={className}>
            <Label className="text-xs font-semibold uppercase tracking-wider text-neutral-600 mb-1.5 block">
                {label}{required && <span className="text-red-600 ml-0.5">*</span>}
            </Label>
            {children}
        </div>
    );
}

function ProductCard({ product, onEdit, onArchive, onDelete, onShowQr, canWrite, canArchive }) {
    const img = product.images?.[0];
    const cs = Number(product.current_stock || 0);
    const mn = Number(product.min_stock || 0);
    const status = cs <= 0 ? "out" : cs <= mn ? "low" : "safe";
    const statusColor = { safe: "#15803D", low: "#F59E0B", out: "#DC2626" }[status];
    const statusBg   = { safe: "#DCFCE7", low: "#FEF3C7", out: "#FEE2E2" }[status];
    const statusText = { safe: "In Stock", low: "Low Stock", out: "Out of Stock" }[status];
    return (
        <div className="bg-white border border-neutral-200 rounded-md overflow-hidden group hover:border-neutral-400 transition-colors fade-up flex flex-col"
             data-testid={`product-card-${product.sku}`}>
            <div className="relative aspect-[4/3] bg-neutral-100 overflow-hidden">
                {img ? (
                    <img src={fileUrl(img)} alt={product.name} className="w-full h-full object-cover" />
                ) : (
                    <div className="w-full h-full flex items-center justify-center text-neutral-300"><ImageSquare size={40} weight="thin" /></div>
                )}
                <span className="absolute top-2 left-2 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded"
                      style={{ background: statusBg, color: statusColor }}>{statusText}</span>
                {!product.is_active && (
                    <span className="absolute top-2 right-2 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded bg-neutral-900 text-white">Archived</span>
                )}
            </div>
            <div className="p-3.5 flex-1 flex flex-col">
                <div className="text-[10px] font-mono text-neutral-500 uppercase tracking-wider">{product.sku}</div>
                <div className="font-semibold text-neutral-900 mt-0.5 leading-tight line-clamp-1">{product.name}</div>
                <div className="mt-2 flex items-center justify-between text-sm">
                    <span className="font-bold tabular font-display" style={{ color: "#15803D" }}>{formatCurrency(product.selling_price)}</span>
                    <span className="text-xs text-neutral-500 tabular">Stock: <b>{cs}</b> / min {mn}</span>
                </div>
                <div className="mt-3 pt-3 border-t border-neutral-100 flex items-center gap-1">
                    {canWrite && (
                        <button onClick={onEdit} className="flex items-center gap-1 text-xs text-neutral-600 hover:text-neutral-900 px-2 py-1"
                                data-testid={`product-edit-${product.sku}`}>
                            <Pencil size={13} /> Edit
                        </button>
                    )}
                    <button onClick={onShowQr} className="flex items-center gap-1 text-xs text-neutral-600 hover:text-neutral-900 px-2 py-1"
                            data-testid={`product-qr-${product.sku}`}>
                        <QrCode size={13} /> QR
                    </button>
                    {canArchive && product.is_active && (
                        <button onClick={onArchive} className="ml-auto flex items-center gap-1 text-xs text-amber-700 hover:text-amber-900 px-2 py-1"
                                data-testid={`product-archive-${product.sku}`}>
                            <Archive size={13} /> Archive
                        </button>
                    )}
                    {canArchive && (
                        <button onClick={onDelete} className={"flex items-center gap-1 text-xs text-red-600 hover:text-red-800 px-2 py-1 " + (product.is_active ? "" : "ml-auto")}
                                data-testid={`product-delete-${product.sku}`}>
                            <X size={13} weight="bold" /> Delete
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

function EmptyState({ onCreate }) {
    return (
        <div className="border border-dashed border-neutral-300 rounded-md py-16 text-center bg-white" data-testid="products-empty">
            <ImageSquare size={40} weight="thin" className="mx-auto text-neutral-300" />
            <div className="mt-3 text-lg font-bold font-display">No products yet</div>
            <div className="text-sm text-neutral-500 mt-1">Add your first product to start managing inventory.</div>
            {onCreate && (
                <Button onClick={onCreate} className="mt-4 text-white" style={{ backgroundColor: "#15803D" }}>
                    <Plus size={14} className="mr-1.5" /> Add Product
                </Button>
            )}
        </div>
    );
}
